import type { DataSnapshot } from 'firebase/database';
import {
  onChildAdded,
  onValue,
  onChildChanged,
  set,
  onDisconnect,
  serverTimestamp,
  update,
  push,
} from 'firebase/database';

import { noop, sleepFor } from '@/common/utilities';
import { devLog } from '@/common/debug';
import { captureEventException, ErrorTrackingEvent } from '@/common/error-tracking';

import { EventEmitter } from '../common/EventEmitter';
import type { VisitorDetail } from '../common/PleasedSdk.types';
import { daemonService } from '../common/DaemonService';
import { TeardownManager } from '../common/Teardown';

import type * as Types from './PleasedChat.types';
import { FirebaseStore } from './FirebaseStore';
import { PleasedInternalError, PleasedError } from './errors';

interface FirebaseServiceOptions {
  visitor: VisitorDetail;
  chatId: number;
  token: string;
}

type FirebaseChatEvents = {
  onMessage: { message: Types.FirebaseChatMessage; key: string };
  onTyping: { store: Types.TypingStore; meta?: Types.ChatTypingEvent };
  onEnd: Types.FirebaseClosedChat;
  onPresence: Types.FirebasePresence | null;
  onDestroy: void;
  onParticipation: Types.MemberParticipation;
  onNetwork: boolean;
  onNetworkError: Error;
};

export interface ChatMember {
  id: number;
  name?: string;
  type: Types.UserKind;
}

export { serverTimestamp };

export class FirebaseChatService extends EventEmitter<FirebaseChatEvents> {
  readonly visitor: VisitorDetail;
  readonly chatId: number;
  protected chatDb: Types.FirebaseDatabaseStore;
  protected $tdm = new TeardownManager();

  protected chatState = {
    areUsersTyping: {} as Types.TypingStore,
    closed: null as null | Types.FirebaseClosedChat,
    members: {} as Record<string | number, ChatMember>,
    messages: {} as Record<string | number, Types.FirebaseChatMessage>,
    isActive: false,
  };

  protected synched = {
    isOnline: false,
    offset: 0,
  };

  /** Sync failure reasons already reported for this chat — see `reportSyncFailure`. */
  protected reportedSyncFailures = new Set<string>();

  get isConnected() {
    return this.synched.isOnline;
  }

  constructor(options: FirebaseServiceOptions) {
    super();
    this.visitor = options.visitor;
    this.chatId = options.chatId;
    this.chatDb = FirebaseStore.get(options.chatId, options.token);
  }

  static create(options: FirebaseServiceOptions) {
    return new FirebaseChatService(options);
  }

  /**
   * A connected chat can stop syncing silently: listeners are cancelled on an expired token and
   * presence writes keep failing on every heartbeat. Report each cause once per chat so the
   * 5s heartbeat cannot flood GlitchTip.
   */
  protected reportSyncFailure(reason: string, error: unknown) {
    if (this.reportedSyncFailures.has(reason)) return;
    this.reportedSyncFailures.add(reason);

    const cause = error instanceof Error ? error.message : String(error);
    devLog.debug('[FIREBASE_CHAT_SERVICE] Sync failure', { chatId: this.chatId, reason, error: cause });
    captureEventException(
      new Error(`[FIREBASE_CHAT_SERVICE] ${reason} failed: ${cause}`),
      ErrorTrackingEvent.ChatFirebaseSync
    );
  }

  /**
   * Sets up Firebase listener for AI chat intervention
   * @param aiSessionId - The AI session ID to monitor
   * @param firebaseToken - Firebase token for authentication
   * @param onIntervene - Callback when intervention occurs
   * @returns Cleanup function
   */
  static setupAiSessionIntervention(aiSessionId: string, firebaseToken: string, onIntervene: (chatId: number) => void) {
    return FirebaseStore.setupAiSessionIntervention(aiSessionId, firebaseToken, onIntervene);
  }

  async start() {
    // If already started, do nothing.
    if (this.chatState.isActive) return;
    this.chatState.isActive = true;
    this.setupDatabaseListeners();
    this.setupLocalListeners();
    this.setupPresence();
    await this.setChatMetadata();
  }

  estimatedServerDate() {
    return new Date(new Date().getTime() + this.synched.offset);
  }

  getChatMember(userId: string | number) {
    return this.chatState.members[userId];
  }

  setupLocalListeners() {
    const { chatDb } = this;

    const removeConnectListener = onValue(
      chatDb.util.connection,
      (snap: DataSnapshot) => {
        this.event.emit('onNetwork', snap.val());
        this.synched.isOnline = snap.val();
      },
      (error) => {
        this.reportSyncFailure('connection listener', error);
        this.event.emit('onNetworkError', error);
      }
    );

    const removeTimestampListener = onValue(
      chatDb.util.timeOffset,
      (snap: DataSnapshot) => {
        this.synched.offset = snap.val();
      },
      (error) => this.reportSyncFailure('time offset listener', error)
    );

    this.$tdm.add(removeConnectListener);
    this.$tdm.add(removeTimestampListener);
  }

  setupDatabaseListeners() {
    const { chatDb } = this;
    // Chat Messages
    onChildAdded(
      chatDb.messages,
      (snap: DataSnapshot) => {
        if (!snap.exists()) return;
        const message: Types.FirebaseChatMessage = snap.val();
        this.receiveNewMessage(message, snap.key as string);
      },
      (error) => this.reportSyncFailure('messages listener', error)
    );

    // Closed chats
    onValue(
      chatDb.closed,
      (snap: DataSnapshot) => {
        if (!snap.exists()) return;
        const data: Types.FirebaseClosedChat = snap.val();
        // A value in this database means that the
        // chat has been closed by someone.
        this.chatState.closed = data;
        this.event.emit('onEnd', data);
      },
      (error) => this.reportSyncFailure('closed chat listener', error)
    );

    const typingListener = (snap: DataSnapshot) => {
      if (!snap.key) return;
      const typing: Types.TypingMetadata = snap.val();
      const isTyping = Boolean(typing?.isTyping);
      const displayName = typing?.displayName || '';
      const userId = +snap.key;
      const type = userId === this.visitor.id ? 'customer' : 'agent';

      const store = (this.chatState.areUsersTyping = { ...this.chatState.areUsersTyping, [userId]: typing });
      this.event.emit('onTyping', { store, meta: { isTyping, displayName, userId, type } });
    };

    const onTypingListenerCancelled = (error: Error) => this.reportSyncFailure('typing listener', error);
    onChildAdded(chatDb.typing, typingListener, onTypingListenerCancelled);
    onChildChanged(chatDb.typing, typingListener, onTypingListenerCancelled);
  }

  async sendMessage(message: Types.FirebaseChatMessage) {
    if (!this.chatState.isActive) return;
    return push(this.chatDb.messages, message)
      .then(noop)
      .catch((err: unknown) =>
        Promise.reject(
          new PleasedInternalError(
            PleasedError.FIREBASE_SEND_MESSAGE_FAILED,
            err instanceof Error ? err.message : String(err)
          )
        )
      );
  }

  /**
   * At the moment, there is no dynamic firebase database that
   * detects if a user has joined, when we receive a message from the agent
   * we are sure that the agent has joined, hence, we manually detect
   * which agent has joined.
   */
  protected detectUserJoined(message: Types.FirebaseChatMessage) {
    if (this.chatState.members[message.userId]) {
      // The user has already joined the chat, nothing to do here.
      return;
    }

    const joinedUser: ChatMember = {
      id: message.userId,
      type: message.from,
      name: message.from === 'customer' ? this.visitor.displayName : message.agentDisplayName || '',
    };

    this.chatState.members[message.userId] = joinedUser;
    // Member joined.
    this.event.emit('onParticipation', {
      type: 'member:join',
      member: {
        userId: message.userId,
        name: joinedUser.name as string,
        timestamp: this.estimatedServerDate().getTime() - 1000,
        type: message.from,
      },
    });
  }

  protected makeMemberLeave(memberId: string | number) {
    const user = this.chatState.members[memberId];
    // User never joined, so no needed to make them leave.
    if (!user) return;

    const member = {
      name: user.name || '',
      timestamp: Date.now(),
      userId: user.id,
      type: user.type,
    };

    this.event.emit('onParticipation', {
      type: 'member:leave',
      member,
    });

    delete this.chatState.members[memberId];
  }

  protected receiveNewMessage(message: Types.FirebaseChatMessage, key: string) {
    this.detectUserJoined(message);

    // Save local copy for future use
    this.chatState.messages[key] = message;
    // Emit a copy of the message.
    this.event.emit('onMessage', { message, key });
  }

  /**
   * Set's the initial metadata required for the chat. This
   * will set the customer details in firebase, which can
   * start the chat.
   */
  protected setChatMetadata() {
    const { chatDb } = this;
    const metadata: Types.FirebaseChatMetadataWrite = {
      customerName: this.visitor.displayName,
      customerId: this.visitor.id as number,
      customerPhone: this.visitor.phone || '',
    };
    return set(chatDb.metadata, metadata);
  }

  protected setupPresence() {
    // Register onDisconnect before going online, and before the heartbeat early-return.
    this.setupPresenceDisconnect();

    // Set initial presence
    const initialPresence: Omit<Types.FirebasePresence, 'lastStatusUpdateTime'> = {
      status: 'online',
      chatId: this.chatId,
      customerId: this.visitor.id as number,
      reason: 'initial',
    };
    const settingPresence = this.setPresence(initialPresence, { force: true });

    // Create a daemon process that will periodically
    // update the lastUpdateTime
    const processName = `sync-presence-${this.chatId}`;

    // If daemon Service is already registered, return the promise.
    if (daemonService.get(processName)) return settingPresence;

    const executable = async () => {
      const presence = this.setPresence({ status: 'online', reason: 'heartbeat' }).catch(noop);
      await Promise.all([sleepFor(5), presence]);
    };

    // Register the daemon service and start it.
    const started = daemonService.register({ name: processName, mode: 'async', executable }).start(processName);
    devLog.debug('[FIREBASE_CHAT_SERVICE] Daemon started', started);

    this.$tdm.add(() => {
      daemonService.remove(processName);
    });

    onValue(
      this.chatDb.presence,
      (snap: DataSnapshot) => {
        if (!snap.exists()) {
          // Chat was cleared after ended, check if chat is not yet ended & end it.
          return;
        }
        const remotePresence: Types.FirebasePresence = snap.val();
        if (!remotePresence.fromDisconnect) return;
        // Client was disconnected & got connected again.
        // So we will setup the disconnect again.
        this.setupPresenceDisconnect();
      },
      (error) => this.reportSyncFailure('presence listener', error)
    );

    return settingPresence;
  }

  stopPresenceHeartbeat() {
    daemonService.remove(`sync-presence-${this.chatId}`);
  }

  cancelPresenceDisconnect() {
    this.$tdm.tearOne('presence-disconnect');
  }

  protected setupPresenceDisconnect() {
    // Cancel any existing onDisconnect handler before creating a new one
    // This prevents accumulation of handlers during repetitive disconnect/reconnect cycles
    this.$tdm.tearOne('presence-disconnect');

    const setOfflinePayload: Partial<Types.FirebasePresence> = {
      status: 'offline',
      fromDisconnect: serverTimestamp(),
    };
    const onPresenceDisconnected = onDisconnect(this.chatDb.presence);
    onPresenceDisconnected.update(setOfflinePayload);

    // Register the cancel function so it can be cleaned up properly
    this.$tdm.add('presence-disconnect', () => onPresenceDisconnected.cancel());
  }

  async setPresence(
    options: Pick<Types.FirebasePresence, 'status'> & Partial<Types.FirebasePresence>,
    opts?: { force?: boolean }
  ) {
    if (!this.chatState.isActive || !this.chatDb?.presence) return;

    // Heartbeats skip writes while disconnected; end-chat offline must still be persisted.
    if (!opts?.force && !this.isConnected) return;

    const presence: Partial<Types.FirebasePresence> = {
      lastStatusUpdateTime: serverTimestamp(),
      reason: 'manual',
      ...options,
    };

    if (options.reason === 'end-chat-local') {
      presence.clickedEndChat = true;
      presence.clickedEndChatAt = serverTimestamp();
    }

    try {
      await update(this.chatDb.presence, presence);
    } catch (error) {
      // The heartbeat swallows its rejection, so this is the only place that sees a dead presence write.
      this.reportSyncFailure(`presence write (${presence.reason})`, error);
      throw error;
    }
  }

  async setTyping(isTyping: boolean) {
    if (!this.chatDb || !this.chatState.isActive) return;
    const typingMetadata: Types.TypingMetadata = {
      displayName: this.visitor.displayName,
      isAgent: false,
      isTyping,
    };
    await set(this.chatDb.typing, { ...this.chatState.areUsersTyping, [this.visitor.id]: typingMetadata }).catch(
      (error: unknown) => this.reportSyncFailure('typing write', error)
    );
  }

  makeAllMembersLeave() {
    Object.values(this.chatState.members).forEach((m) => {
      this.makeMemberLeave(m.id);
    });
  }

  async makeSureNoOneIsTyping() {
    const anyoneTyping = Object.values(this.chatState.areUsersTyping).some((t) => t.isTyping);
    if (!anyoneTyping) return;

    // Check if user is still typing.
    const isUserStillTyping = Object.values(this.chatState.areUsersTyping).find((t) => !t.isAgent && t.isTyping);

    const typingEntries = Object.entries(this.chatState.areUsersTyping).map(([userId, typing]) => [
      userId,
      { ...typing, isTyping: false },
    ]);
    this.chatState.areUsersTyping = Object.fromEntries(typingEntries);

    this.event.emit('onTyping', { store: this.chatState.areUsersTyping });

    return isUserStillTyping && this.setTyping(false);
  }

  destroy() {
    this.chatState.isActive = false;
    this.event.all.clear();
    FirebaseStore.destroy(this.chatDb);
    this.$tdm.teardown();
  }
}
